import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Tailwind width class for the panel (default `max-w-md`). */
  maxWidth?: string;
  /** When true, renders a single ✕ button in the panel header. */
  showClose?: boolean;
}

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message?: string;
  confirmLabel: string;
  /** `danger` (red) for destructive actions, `default` (indigo) otherwise. */
  variant?: 'default' | 'danger';
  /** While busy the confirm button is disabled and shows "Procesando...". */
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const FOCUSABLE_SELECTOR = [
  'button',
  '[href]',
  'input',
  'select',
  'textarea',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Accessible dialog wrapper shared by the PO pages. Implements the non-modal
 * a11y contract without any library:
 * - `role="dialog"` + `aria-modal` + title wired via `aria-labelledby`
 * - Esc closes (listener only active while open)
 * - Focus trap: Tab/Shift+Tab cycle within the panel
 * - On open, focus moves to the first focusable element (or the panel);
 *   on close, focus returns to the previously focused element
 * - Backdrop click closes; panel stops propagation
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  maxWidth = 'max-w-md',
  showClose = false,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Esc closes. Listener is attached only while the dialog is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // On open: move focus into the dialog (first focusable, else the panel).
  // On close: restore focus to whatever had it before opening.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (panel) {
      const first = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? panel).focus();
    }
    return () => {
      previouslyFocused?.focus?.();
    };
  }, [open]);

  // Minimal focus trap: cycle Tab/Shift+Tab within the panel.
  const handlePanelKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusables.length === 0) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handlePanelKeyDown}
        onClick={(e) => e.stopPropagation()}
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-h-[85vh] overflow-y-auto p-6 ${maxWidth}`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 id={titleId} className="text-lg font-bold">
            {title}
          </h3>
          {showClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="text-gray-500 hover:text-gray-700 dark:text-gray-300 text-xl min-h-[44px] min-w-[44px]"
            >
              ✕
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Accessible replacement for the native confirm() dialogs on the PO pages.
 * Keeps the original es-AR copy as title/message; backfill only the buttons.
 */
export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  variant = 'default',
  busy = false,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  const confirmClass =
    variant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700';
  return (
    <Modal open={open} onClose={onCancel} title={title} maxWidth="max-w-sm" showClose>
      {message && <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">{message}</p>}
      <div className="flex gap-3 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 dark:border-gray-600 min-h-[44px] disabled:opacity-70"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-70 min-h-[44px] ${confirmClass}`}
        >
          {busy ? 'Procesando...' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}